import {CustomerApi} from '@/common/api/CustomerApi';
import {useApiClient} from '@/common/api/useApiClient';
import {BaseApi} from '@/common/api/BaseApi';

const STYLE_TAG_ID = 'customer-style';
const FAVICON_TAG_PREFIX = 'customer-favicon-';

// Mapping per size → how the tag in <head> needs to look. Mirrors the PHP
// layout/favicon.php convention: 32 = generic icon, 180 = apple-touch,
// 192 = PWA icon, 270 = Windows Metro Tile.
const FAVICON_TAGS: Record<string, {tag: 'link' | 'meta'; attrs: Record<string, string>}> = {
  '32x32': {tag: 'link', attrs: {rel: 'icon', sizes: '32x32', type: 'image/jpeg'}},
  '180x180': {tag: 'link', attrs: {rel: 'apple-touch-icon', sizes: '180x180'}},
  '192x192': {tag: 'link', attrs: {rel: 'icon', sizes: '192x192', type: 'image/jpeg'}},
  '270x270': {tag: 'meta', attrs: {name: 'msapplication-TileImage'}},
};

interface CustomerStyleResponse {
  style: string;
  favicons?: Record<string, string>;
}

function extractSubdomain(): string | null {
  if (process.env.NODE_ENV !== 'production') {
    return 'dev';
  }
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length < 3) {
    return null;
  }
  return parts[0];
}

function applyStyle(style: string): void {
  let styleTag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!styleTag) {
    styleTag = document.createElement('style');
    styleTag.id = STYLE_TAG_ID;
    document.head.appendChild(styleTag);
  }
  styleTag.textContent = style;
}

function applyFavicons(favicons: Record<string, string>): void {
  // Remove existing customer-favicon tags so nothing stale lingers on a
  // subdomain change or repeated call.
  document.querySelectorAll(`[id^="${FAVICON_TAG_PREFIX}"]`).forEach((node) => node.remove());

  for (const [size, relativePath] of Object.entries(favicons)) {
    const config = FAVICON_TAGS[size];
    if (!config) {
      continue;
    }
    const element = document.createElement(config.tag);
    element.id = `${FAVICON_TAG_PREFIX}${size}`;
    for (const [key, value] of Object.entries(config.attrs)) {
      element.setAttribute(key, value);
    }
    const href = `${BaseApi.IMAGE_URL}${relativePath}`;
    if (config.tag === 'link') {
      element.setAttribute('href', href);
    } else {
      element.setAttribute('content', href);
    }
    document.head.appendChild(element);
  }
}

export async function loadCustomerStyle(): Promise<void> {
  const apiClient = useApiClient();
  // L-6 — the backend resolves the tenant from the Referer header by
  // default; we only fall back to the explicit subdomain query parameter
  // in dev so the dev script doesn't need a Referer-emitting browser.
  const subdomain = process.env.NODE_ENV !== 'production' ? extractSubdomain() : null;
  const response = await apiClient.get<CustomerStyleResponse>(
    subdomain ? CustomerApi.style({subdomain}) : CustomerApi.styleByReferer(),
    false
  ).catch(() => null);
  if (!response || response.status !== 200 || !response.data?.style) {
    return;
  }

  applyStyle(response.data.style);
  if (response.data.favicons) {
    applyFavicons(response.data.favicons);
  }
}
